import React, { useEffect, useRef, useState } from "react";
import { InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useAwardSearch from "./useAwardSearch";

// A text input bound to a single URL search param. Typing debounces into the
// URL (replace:true so we don't flood history); Enter flushes immediately. This
// backs both the awards name box and the awardees first/last-name boxes, so the
// debounce/sync logic lives in one place instead of being copied per field.
export default function DebouncedField({
  paramKey,
  placeholder,
  icon = false,
  className = "awards-search-field",
}) {
  const { params, setParams } = useAwardSearch();
  const current = params[paramKey];
  const [term, setTerm] = useState(current);
  const lastPushed = useRef(current);

  // Adopt external changes (Clear, tab switch) without clobbering mid-typing.
  useEffect(() => {
    if (current !== lastPushed.current) {
      setTerm(current);
      lastPushed.current = current;
    }
  }, [current]);

  // Debounce typing -> URL.
  useEffect(() => {
    if (term === current) return;
    const t = setTimeout(() => {
      lastPushed.current = term;
      setParams({ [paramKey]: term, page: 1 }, { replace: true });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const submitNow = () => {
    lastPushed.current = term;
    setParams({ [paramKey]: term, page: 1 });
  };

  return (
    <TextField
      className={className}
      size="small"
      placeholder={placeholder}
      value={term}
      onChange={(e) => setTerm(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && submitNow()}
      InputProps={
        icon
          ? {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }
          : undefined
      }
    />
  );
}
